import { FC, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import {
  TherapistStatusCounts,
  TherapistFilters,
  TherapistTable,
  StatusActionModal,
  ActionType,
} from '../../components/admin/therapist';
import { PermissionGuard } from '../../components/admin/PermissionGuard';
import { useToast } from '../../components/ui/Toast/ToastContext';
import {
  useTherapistList,
  useTherapistStatusCounts,
  useApproveTherapist,
  useRejectTherapist,
  useSuspendTherapist,
  useReactivateTherapist,
} from '../../hooks/admin/useAdminTherapists';
import type { TherapistListParams, TherapistStatus, AdminTherapistListItem } from '../../api/adminTherapistApi';

export const TherapistsListPage: FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Query params state
  const [params, setParams] = useState<TherapistListParams>({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    action: ActionType;
    therapist: AdminTherapistListItem | null;
  }>({
    isOpen: false,
    action: 'approve',
    therapist: null,
  });

  // Queries
  const { data: therapistsData, isLoading, refetch } = useTherapistList(params);
  const { data: statusCounts, isLoading: isLoadingCounts } = useTherapistStatusCounts();

  // Mutations
  const approveMutation = useApproveTherapist();
  const rejectMutation = useRejectTherapist();
  const suspendMutation = useSuspendTherapist();
  const reactivateMutation = useReactivateTherapist();

  const isProcessing =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    suspendMutation.isPending ||
    reactivateMutation.isPending;

  // Handlers
  const handleSearchChange = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleStatusFilterChange = useCallback((status: string) => {
    const typedStatus = status as TherapistStatus | '';
    setParams((prev) => ({ ...prev, status: typedStatus || undefined, page: 1 }));
  }, []);

  const handleSortByChange = useCallback((sortBy: string) => {
    setParams((prev) => ({ ...prev, sortBy: sortBy as TherapistListParams['sortBy'], page: 1 }));
  }, []);

  const handleSortOrderChange = useCallback((sortOrder: 'asc' | 'desc') => {
    setParams((prev) => ({ ...prev, sortOrder, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const handleRowClick = useCallback(
    (therapist: AdminTherapistListItem) => {
      navigate(`/admin/therapists/${therapist.id}`);
    },
    [navigate]
  );

  const openModal = useCallback((action: ActionType, therapist: AdminTherapistListItem) => {
    setModalState({ isOpen: true, action, therapist });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({ isOpen: false, action: 'approve', therapist: null });
  }, []);

  const handleModalConfirm = useCallback(
    async (data: { reason?: string; notes?: string; cancelFutureBookings?: boolean }) => {
      const { action, therapist } = modalState;
      if (!therapist) return;

      try {
        switch (action) {
          case 'approve':
            await approveMutation.mutateAsync({ id: therapist.id, notes: data.notes });
            showToast({ type: 'success', message: `${therapist.name} has been approved` });
            break;
          case 'reject':
            await rejectMutation.mutateAsync({ id: therapist.id, reason: data.reason! });
            showToast({ type: 'success', message: `${therapist.name} has been rejected` });
            break;
          case 'suspend':
            await suspendMutation.mutateAsync({
              id: therapist.id,
              reason: data.reason!,
              cancelFutureBookings: data.cancelFutureBookings,
            });
            showToast({ type: 'success', message: `${therapist.name} has been suspended` });
            break;
          case 'reactivate':
            await reactivateMutation.mutateAsync({ id: therapist.id, notes: data.notes });
            showToast({ type: 'success', message: `${therapist.name} has been reactivated` });
            break;
        }
        closeModal();
      } catch (error) {
        showToast({
          type: 'error',
          message: error instanceof Error ? error.message : `Failed to ${action} therapist`,
        });
      }
    },
    [modalState, approveMutation, rejectMutation, suspendMutation, reactivateMutation, showToast, closeModal]
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              Therapist Management
            </h1>
            <p className="mt-1 text-gray-500 dark:text-slate-400">
              Manage therapist applications and status
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <PermissionGuard permission="MANAGE_THERAPISTS">
              <button
                onClick={() => navigate('/admin/therapists/new')}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Add Therapist
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Status Counts */}
        <TherapistStatusCounts
          counts={statusCounts}
          isLoading={isLoadingCounts}
          activeFilter={params.status || ''}
          onFilterClick={handleStatusFilterChange}
        />

        {/* Filters */}
        <TherapistFilters
          search={params.search || ''}
          onSearchChange={handleSearchChange}
          status={params.status || ''}
          onStatusChange={handleStatusFilterChange}
          sortBy={params.sortBy || 'createdAt'}
          onSortByChange={handleSortByChange}
          sortOrder={params.sortOrder || 'desc'}
          onSortOrderChange={handleSortOrderChange}
        />

        {/* Table */}
        <TherapistTable
          therapists={therapistsData?.data || []}
          pagination={therapistsData?.pagination}
          isLoading={isLoading}
          onPageChange={handlePageChange}
          onRowClick={handleRowClick}
          onApprove={(t) => openModal('approve', t)}
          onReject={(t) => openModal('reject', t)}
          onSuspend={(t) => openModal('suspend', t)}
          onReactivate={(t) => openModal('reactivate', t)}
        />

        {/* Status Action Modal */}
        {modalState.isOpen && modalState.therapist && (
          <StatusActionModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            onConfirm={handleModalConfirm}
            action={modalState.action}
            therapistName={modalState.therapist.name}
            isLoading={isProcessing}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default TherapistsListPage;
